import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Download, Users, Eye, MapPin } from "lucide-react";
import { API } from "../constants";
import Logo from "../components/Logo";

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
};

const Admin = () => {
    const [auth, setAuth] = useState(null); // null = checking, false = logged out, object = admin
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [leads, setLeads] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [stats, setStats] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [tab, setTab] = useState("early");

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then(({ data }) => setAuth(data))
            .catch(() => setAuth(false));
    }, []);

    const loadData = async () => {
        setLoadingData(true);
        try {
            const [leadsRes, statsRes, enqRes] = await Promise.all([
                axios.get(`${API}/admin/leads`, { withCredentials: true }),
                axios.get(`${API}/admin/stats`, { withCredentials: true }),
                axios.get(`${API}/admin/pilot-enquiries`, { withCredentials: true }),
            ]);
            setLeads(leadsRes.data.leads);
            setStats(statsRes.data);
            setEnquiries(enqRes.data.enquiries);
        } catch {
            setAuth(false);
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        if (auth) loadData();
    }, [auth]);

    const login = async (e) => {
        e.preventDefault();
        setLoginError("");
        setLoggingIn(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
            setAuth(data);
        } catch (err) {
            const d = err.response?.data?.detail;
            setLoginError(typeof d === "string" ? d : "Login failed. Please try again.");
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        setAuth(false);
    };

    const exportCsv = async () => {
        const res = await axios.get(`${API}/admin/leads/export`, { withCredentials: true, responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sevano-leads.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (auth === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F4F8FF]">
                <Loader2 className="animate-spin text-[#0B5CF5]" size={32} />
            </div>
        );
    }

    if (auth === false) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F4F8FF] px-4" data-testid="admin-login-screen">
                <form
                    onSubmit={login}
                    className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-[0_30px_80px_-30px_rgba(11,92,245,0.35)]"
                    data-testid="admin-login-form"
                >
                    <Logo />
                    <h1 className="mt-6 text-2xl font-extrabold text-slate-900 tracking-tight">Admin sign in</h1>
                    <p className="mt-1 text-sm text-slate-500">Sevano early-access leads dashboard</p>
                    <div className="mt-8 flex flex-col gap-4">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Admin email"
                            data-testid="admin-email-input"
                            className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-sm outline-none focus:border-[#0B5CF5] focus:ring-4 focus:ring-[#0B5CF5]/10 transition-all"
                        />
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            data-testid="admin-password-input"
                            className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-sm outline-none focus:border-[#0B5CF5] focus:ring-4 focus:ring-[#0B5CF5]/10 transition-all"
                        />
                        {loginError && (
                            <p className="text-sm font-semibold text-red-600 bg-red-50 rounded-xl px-4 py-3" role="alert" data-testid="admin-login-error">
                                {loginError}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={loggingIn}
                            data-testid="admin-login-submit-button"
                            className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm disabled:opacity-60"
                        >
                            {loggingIn ? <Loader2 size={17} className="animate-spin" /> : "Sign in"}
                        </button>
                        <a href="/" className="text-center text-xs font-semibold text-slate-400 hover:text-[#0B5CF5] transition-colors">
                            ← Back to sevano.in
                        </a>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F4F8FF]" data-testid="admin-dashboard">
            <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Logo />
                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportCsv}
                            data-testid="admin-export-button"
                            className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs"
                        >
                            <Download size={14} /> Export CSV
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors"
                        >
                            <LogOut size={14} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="rounded-3xl bg-white p-6 border border-slate-100" data-testid="admin-stat-visits">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
                            <Eye size={14} className="text-[#0B5CF5]" /> Page visits
                        </div>
                        <p className="text-3xl font-extrabold text-slate-900">{stats?.visits ?? "—"}</p>
                    </div>
                    <div className="rounded-3xl bg-white p-6 border border-slate-100" data-testid="admin-stat-signups">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
                            <Users size={14} className="text-[#0B5CF5]" /> Early access signups
                        </div>
                        <p className="text-3xl font-extrabold text-slate-900">{stats?.signups ?? "—"}</p>
                    </div>
                    <div className="rounded-3xl bg-white p-6 border border-slate-100" data-testid="admin-stat-pilot">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
                            <Users size={14} className="text-[#0B5CF5]" /> Pilot enquiries
                        </div>
                        <p className="text-3xl font-extrabold text-slate-900">{stats?.pilot_enquiries ?? "—"}</p>
                    </div>
                    <div className="rounded-3xl bg-white p-6 border border-slate-100" data-testid="admin-stat-localities">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">
                            <MapPin size={14} className="text-[#0B5CF5]" /> Top localities
                        </div>
                        <ul className="space-y-1">
                            {(stats?.localities || []).slice(0, 4).map((l) => (
                                <li key={l.locality} className="flex justify-between text-sm text-slate-600">
                                    <span>{l.locality}</span>
                                    <span className="font-bold text-slate-900">{l.count}</span>
                                </li>
                            ))}
                            {stats && stats.localities.length === 0 && <li className="text-sm text-slate-400">No signups yet</li>}
                        </ul>
                    </div>
                </div>

                <div className="rounded-3xl bg-white border border-slate-100 overflow-hidden">
                    <div className="px-6 pt-5 border-b border-slate-100 flex flex-wrap items-end justify-between gap-3">
                        <div className="flex gap-2" role="tablist">
                            <button
                                role="tab"
                                aria-selected={tab === "early"}
                                data-testid="admin-tab-early-access"
                                onClick={() => setTab("early")}
                                className={`px-4 py-2.5 text-sm font-bold rounded-t-2xl border-b-2 transition-colors ${
                                    tab === "early"
                                        ? "text-[#0B5CF5] border-[#0B5CF5]"
                                        : "text-slate-400 border-transparent hover:text-slate-600"
                                }`}
                            >
                                Early Access
                            </button>
                            <button
                                role="tab"
                                aria-selected={tab === "pilot"}
                                data-testid="admin-tab-pilot-enquiries"
                                onClick={() => setTab("pilot")}
                                className={`px-4 py-2.5 text-sm font-bold rounded-t-2xl border-b-2 transition-colors ${
                                    tab === "pilot"
                                        ? "text-[#0B5CF5] border-[#0B5CF5]"
                                        : "text-slate-400 border-transparent hover:text-slate-600"
                                }`}
                            >
                                Pilot Enquiries
                            </button>
                        </div>
                        <span className="text-xs font-semibold text-slate-400 pb-2.5">
                            {tab === "early" ? `${leads.length} total` : `${enquiries.length} total`}
                        </span>
                    </div>
                    {loadingData ? (
                        <div className="p-12 flex justify-center">
                            <Loader2 className="animate-spin text-[#0B5CF5]" size={28} />
                        </div>
                    ) : tab === "early" ? (
                        leads.length === 0 ? (
                            <p className="p-12 text-center text-sm text-slate-400" data-testid="admin-leads-empty">
                                No leads yet. Share the page and they'll appear here.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="admin-leads-table">
                                    <thead>
                                        <tr className="text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                                            <th className="px-6 py-3">Name</th>
                                            <th className="px-6 py-3">Mobile</th>
                                            <th className="px-6 py-3">Locality</th>
                                            <th className="px-6 py-3">Email</th>
                                            <th className="px-6 py-3">Joined</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.map((l) => (
                                            <tr key={l.lead_id} className="border-b border-slate-50 hover:bg-[#F8FAFC] transition-colors">
                                                <td className="px-6 py-3.5 font-semibold text-slate-900">{l.name}</td>
                                                <td className="px-6 py-3.5 text-slate-600">+91 {l.mobile}</td>
                                                <td className="px-6 py-3.5 text-slate-600">{l.locality}</td>
                                                <td className="px-6 py-3.5 text-slate-600">{l.email || "—"}</td>
                                                <td className="px-6 py-3.5 text-slate-500">{fmt(l.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : enquiries.length === 0 ? (
                        <p className="p-12 text-center text-sm text-slate-400" data-testid="admin-enquiries-empty">
                            No pilot enquiries yet. They'll appear here when visitors request a service.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-enquiries-table">
                                <thead>
                                    <tr className="text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                                        <th className="px-6 py-3">Locality</th>
                                        <th className="px-6 py-3">Name</th>
                                        <th className="px-6 py-3">Mobile</th>
                                        <th className="px-6 py-3">Email</th>
                                        <th className="px-6 py-3">Received</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {enquiries.map((en) => (
                                        <tr key={en.enquiry_id} className="border-b border-slate-50 hover:bg-[#F8FAFC] transition-colors">
                                            <td className="px-6 py-3.5 font-semibold text-slate-900">{en.locality}</td>
                                            <td className="px-6 py-3.5 text-slate-600">{en.name}</td>
                                            <td className="px-6 py-3.5 text-slate-600">+91 {en.mobile}</td>
                                            <td className="px-6 py-3.5 text-slate-600">{en.email || "—"}</td>
                                            <td className="px-6 py-3.5 text-slate-500">{fmt(en.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Admin;
